import React, { FC } from 'react';
import { Button, Checkbox, FormField, Heading, Pane, SelectField, Text, TextInputField } from 'evergreen-ui';
import { FiUserX } from 'react-icons/fi';
import { states } from '../consts/states.const';
import { propsForChecked, propsForValue } from '../controllers/form.controller';
import { useFlags } from '../feature-flags/main';

type PropChangeEvent = (name: string, value: string | null) => void;

function makeHtmlChangeEvent(onChange: PropChangeEvent) {
  return (e: React.ChangeEvent<{ name: string; value: string }>) => onChange(e.target.name, e.target.value);
}

export interface MemberComponentProps {
  isPrimary: boolean;
  isPrimaryCancelTerminated?: boolean;
  planEndDate?: string;
  onChange: PropChangeEvent;
  onTerminate: () => void;
  onCancelTerminate: () => void;
  onRemoveCancelTerminate: () => void;
}

export const MemberComponent: FC<MemberComponentProps> = props => {
  const { 'membership-single-source-of-truth-in-eligibility': membershipSingleSourceOfTruthInEligibility } = useFlags();
  const onChange = makeHtmlChangeEvent(props.onChange);

  const benefitStartDateProps = propsForValue('benefitStartDate');
  const benefitEndDateProps = propsForValue('benefitEndDate');
  const isCancelTerminated = propsForValue('isCancelTerminated');
  const isPastTerminated = propsForValue('isPastTerminated');
  const isTerminated = isPastTerminated?.value || isCancelTerminated?.value;
  const planEndDate = props.planEndDate?.split('T')[0];
  const benefitEndDateIsPlanEndDate = benefitEndDateProps?.value && benefitEndDateProps?.value === planEndDate;
  const disableEndDate = Boolean(isPastTerminated?.value);

  return (
    <>
      <TextInputField {...propsForValue('firstName')} onChange={onChange} flex="30%" />
      <TextInputField {...propsForValue('lastName')} onChange={onChange} flex="30%" />

      <TextInputField {...propsForValue('birthDate')} onChange={onChange} type="date" flex="0 0 135px" />

      <TextInputField
        {...propsForValue('ssn')}
        onChange={onChange}
        required={!membershipSingleSourceOfTruthInEligibility || props.isPrimary}
        flex="1 0 110px"
      />

      <TextInputField {...propsForValue('email')} type="email" onChange={onChange} flex="50%" />
      <TextInputField {...propsForValue('phone')} type="phone" onChange={onChange} flex="0 0 120px" />

      {(!membershipSingleSourceOfTruthInEligibility || !isTerminated) && (
        <TextInputField {...benefitStartDateProps} onChange={onChange} type="date" />
      )}

      {!isCancelTerminated?.value && benefitEndDateProps?.value && (
        <>
          <TextInputField {...benefitEndDateProps} onChange={onChange} disabled={disableEndDate} type="date" />
          {!isPastTerminated?.value && benefitEndDateIsPlanEndDate && (
            <Pane flex="40%" display="flex" flexDirection="column" gap={4}>
              <Text size={300}>
                This member's benefit end date is the same as the plan end date. To terminate earlier, change the
                benefit end date.
              </Text>
              <Button width="fit-content" alignSelf="center" onClick={props.onTerminate}>
                <Text size={300} color="blue" is="span">
                  Set benefit end date to today's date
                </Text>
              </Button>
            </Pane>
          )}
        </>
      )}

      {membershipSingleSourceOfTruthInEligibility &&
        isPastTerminated?.value &&
        !isCancelTerminated?.value &&
        !props.isPrimaryCancelTerminated && (
          <FormField {...benefitEndDateProps} label="&nbsp;" marginBottom={24}>
            <Button onClick={props.onRemoveCancelTerminate}>Reinstate eligibility</Button>
          </FormField>
        )}

      {membershipSingleSourceOfTruthInEligibility && isCancelTerminated?.value && (
        <Pane flex="100%">
          <Heading size={400} marginBottom={8}>
            Benefit Eligibility
          </Heading>
          <Text>Eligibility Canceled</Text>
          {!props.isPrimaryCancelTerminated && (
            <Pane marginTop={4}>
              <Button onClick={props.onRemoveCancelTerminate}>Reinstate eligibility</Button>
            </Pane>
          )}
        </Pane>
      )}

      {membershipSingleSourceOfTruthInEligibility && !isTerminated && !benefitEndDateProps?.value && (
        <Button
          name="set-benefit-end-date"
          iconBefore={<FiUserX />}
          intent="danger"
          onClick={props.onTerminate}
          alignSelf="center"
        >
          Set Benefit End Date
        </Button>
      )}

      {membershipSingleSourceOfTruthInEligibility && !isTerminated && (
        <Pane display="flex" flex="0 1 300px" flexDirection="column" gap={4} marginBottom={24}>
          <Button name="cancel-terminate" iconBefore={<FiUserX />} intent="danger" onClick={props.onCancelTerminate}>
            Cancel eligibility
          </Button>
          <Text size={300} color="muted">
            Cancel a member's eligibility if they terminated prior to the benefit start date.
          </Text>
        </Pane>
      )}

      {!membershipSingleSourceOfTruthInEligibility && !benefitEndDateProps?.value && (
        <FormField {...benefitEndDateProps} label="&nbsp;" labelFor="terminate" marginBottom={24}>
          <Button name="terminate" iconBefore={<FiUserX />} intent="danger" onClick={props.onTerminate}>
            Terminate
          </Button>
        </FormField>
      )}
    </>
  );
};
export interface AddressComponentProps {
  onChange: PropChangeEvent;
  required?: boolean;
  children?: never;
}

export const AddressComponent: FC<AddressComponentProps> = props => {
  const onChange = makeHtmlChangeEvent(props.onChange);
  const addressLine1Props = propsForValue('addressLine1');
  const stateProps = propsForValue('state');
  const required = addressLine1Props?.required || props.required;

  return (
    <>
      <TextInputField {...addressLine1Props} required={required} onChange={onChange} flex="100%" marginBottom={0} />
      <TextInputField {...propsForValue('addressLine2')} label="" onChange={onChange} flex="100%" />

      <TextInputField {...propsForValue('city')} required={required} onChange={onChange} flex="50%" />
      <SelectField {...stateProps} required={required} onChange={onChange} flex="60px">
        {!stateProps?.value ? <option></option> : null}
        {states.map(state => (
          <option key={state} value={state}>
            {state}
          </option>
        ))}
      </SelectField>
      <TextInputField {...propsForValue('zipCode')} required={required} onChange={onChange} flex="80px" />
    </>
  );
};

export interface DependentAddressComponentProps extends AddressComponentProps {
  onChangeIsAddressSameAsPrimary: (value: boolean) => void;
}

export const DependentAddressComponent: FC<DependentAddressComponentProps> = ({
  onChangeIsAddressSameAsPrimary,
  ...rest
}) => {
  const isAddressSameAsPrimaryProps = propsForChecked('isAddressSameAsPrimary');

  return (
    <>
      <Checkbox
        {...isAddressSameAsPrimaryProps}
        onChange={e => onChangeIsAddressSameAsPrimary(e.target.checked)}
        flex="0 0 100%"
      />

      {!isAddressSameAsPrimaryProps?.checked ? <AddressComponent {...rest} required /> : null}
    </>
  );
};
